import { useEffect, useState } from 'react';
import { Flex, Button, Dialog, IconButton, Card, Text, TextField, Switch } from '@radix-ui/themes';
import { GearIcon, Share2Icon } from '@radix-ui/react-icons';
import type { LanguagePickerProps } from './LanguagePicker';
import { LanguagePicker } from './LanguagePicker';
import { localize } from '../localize';
import { disableReviewReminders, enableReviewReminders, isPushEnabled } from '../push';
import { clearSyncSession, getSyncSession, startSync, verifySync } from '../sync';
import './Settings.css';

export type SettingsProps = {
  defaultOpen: boolean;
  forceOpen?: boolean;
  onDone: () => Promise<void>;
  onClearHistory: () => Promise<void>;
  onCopyState: () => string;
  onPasteState: (raw: string) => Promise<void>;
  onSyncChange?: () => void;
  onSyncNow?: () => Promise<void>;
  onPushToggle?: (enabled: boolean) => void;
  lastSyncAt?: string | null;
  lastSyncError?: string | null;
  doneText: string;
  source: LanguagePickerProps['value'];
  language: LanguagePickerProps['value'];
  onSourceChange: LanguagePickerProps['onChange'];
  onLanguageChange: LanguagePickerProps['onChange'];
  recordMode: 'auto' | 'button';
  onRecordModeChange: (mode: 'auto' | 'button') => void;
  nextReviewAt: number | null;
  reviewCount: number;
};

// TODO: Add refresh button to the options
export function Settings({
  onDone,
  source,
  language,
  onSourceChange,
  onLanguageChange,
  defaultOpen,
  forceOpen,
  doneText,
  nextReviewAt,
  onClearHistory,
  onCopyState,
  onPasteState,
  onSyncChange,
  onSyncNow,
  onPushToggle,
  lastSyncAt,
  lastSyncError,
  recordMode,
  onRecordModeChange,
}: SettingsProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [pushEnabled, setPushEnabled] = useState(isPushEnabled());
  const [syncPhone, setSyncPhone] = useState(getSyncSession()?.phone ?? '');
  const [smsCode, setSmsCode] = useState('');
  const [loginRequested, setLoginRequested] = useState(false);
  const [smsCodeCountdown, setSmsCodeCountdown] = useState(0);
  const [suppressPermissionCheck, setSuppressPermissionCheck] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [stateMessage, setStateMessage] = useState<string | null>(null);
  const [syncingNow, setSyncingNow] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showDataDetails, setShowDataDetails] = useState(false);
  const isStandalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as any).standalone === true;
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const normalizePhoneInput = (value: string) => {
    const normalized = value.trim().replace(/[^\d+]/g, '');
    if (/^\d{10}$/.test(normalized)) {
      return `+1${normalized}`;
    }
    return value;
  };

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
    }
  }, [forceOpen]);

  useEffect(() => {
    if (loginRequested) {
      setShowLogin(true);
    }
  }, [loginRequested]);

  useEffect(() => {
    let mounted = true;

    const checkMicPermission = async () => {
      try {
        if (!('permissions' in navigator)) {
          return;
        }
        const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (!mounted) {
          return;
        }
        if (status.state === 'granted') {
          setSuppressPermissionCheck(false);
        }
        if (!suppressPermissionCheck && status.state !== 'granted') {
          setIsOpen(true);
        }
      } catch (error) {
        console.warn('Failed to check microphone permissions', error);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        checkMicPermission();
      }
    };

    window.addEventListener('focus', checkMicPermission);
    document.addEventListener('visibilitychange', onVisible);
    checkMicPermission();

    return () => {
      mounted = false;
      window.removeEventListener('focus', checkMicPermission);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isOpen, suppressPermissionCheck]);

  const onDoneClick = async () => {
    setIsOpen(false);
    setSuppressPermissionCheck(true);
    await onDone();
  };

  const onTogglePush = async () => {
    try {
      if (pushEnabled) {
        await disableReviewReminders();
        setPushEnabled(false);
        onPushToggle?.(false);
      } else {
        await enableReviewReminders(nextReviewAt);
        setPushEnabled(true);
        onPushToggle?.(true);
      }
    } catch (error) {
      console.warn('Failed to update reminders', error);
      setPushEnabled(isPushEnabled());
    }
  };

  const onClearClick = async () => {
    if (!window.confirm(localize('Clear conversation history?'))) {
      return;
    }
    await onClearHistory();
  };

  const onCopyStateClick = async () => {
    const snapshot = onCopyState();
    try {
      await navigator.clipboard.writeText(snapshot);
      setStateMessage(localize('State copied'));
    } catch (error) {
      console.warn('Failed to copy state', error);
      window.prompt(localize('Copy conversation'), snapshot);
    }
  };

  const onPasteStateClick = async () => {
    let raw = '';
    try {
      raw = await navigator.clipboard.readText();
    } catch (error) {
      console.warn('Failed to read clipboard', error);
      raw = window.prompt(localize('Paste conversation')) ?? '';
    }
    if (!raw.trim()) {
      return;
    }
    try {
      await onPasteState(raw);
      setStateMessage(localize('State imported'));
    } catch (error) {
      console.warn('Failed to import state', error);
      setStateMessage(localize('Failed to import state'));
    }
  };

  const onSyncStart = async () => {
    try {
      await startSync(syncPhone);
      setSyncError(null);
      setLoginRequested(true);
      setSmsCodeCountdown(60);
      onSyncChange?.();
    } catch (error) {
      console.warn('Failed to start sync', error);
      const message = error instanceof Error ? error.message : localize('Failed to send code');
      setSyncError(message);
    }
  };

  const onSmsCodeVerify = async () => {
    try {
      await verifySync(syncPhone, smsCode);
      setSyncError(null);
      setSmsCode('');
      setLoginRequested(false);
      setSmsCodeCountdown(0);
      onSyncChange?.();
    } catch (error) {
      console.warn('Failed to verify sync', error);
      const message = error instanceof Error ? error.message : localize('Failed to verify code');
      setSyncError(message);
    }
  };

  const onSyncDisconnect = () => {
    clearSyncSession();
    setSmsCode('');
    setLoginRequested(false);
    setSmsCodeCountdown(0);
    onSyncChange?.();
  };

  const onSyncNowClick = async () => {
    if (!onSyncNow) {
      return;
    }
    setSyncingNow(true);
    try {
      await onSyncNow();
    } finally {
      setSyncingNow(false);
    }
  };

  const formatSyncTime = (value: string | null | undefined, isSyncing: boolean) => {
    if (isSyncing) {
      return localize('Syncing...');
    }
    if (!value) {
      return localize('Never');
    }
    const timestamp = Date.parse(value);
    if (Number.isNaN(timestamp)) {
      return localize('Never');
    }
    return new Date(timestamp).toLocaleString();
  };

  useEffect(() => {
    if (!loginRequested || smsCodeCountdown <= 0) {
      return;
    }
    const timer = window.setInterval(() => {
      setSmsCodeCountdown((value) => Math.max(value - 1, 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [loginRequested, smsCodeCountdown]);

  const syncSession = getSyncSession();
  const isSynced = !!syncSession;
  const showSyncingLabel = isSynced && !lastSyncAt && !lastSyncError;

  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Trigger>
        <IconButton variant='ghost' color="gray" size="4" radius="full">
          <GearIcon onClick={() => setIsOpen(true)} width="22" height="22" />
        </IconButton>
      </Dialog.Trigger>

      <Dialog.Content style={{ maxWidth: 450 }} aria-describedby={localize('Select a language')}>
        <Dialog.Title>
          <GearIcon width="22" height="22" style={{ marginBottom: '-0.25rem' }} /> {localize('Choose a language')}
        </Dialog.Title>
        <Flex direction="column" gap="4">
          <Card>
            <LanguagePicker value={source} onChange={onSourceChange} />
          </Card>
          <Card>
            <Flex align="center" justify="between" gap="4">
              <LanguagePicker value={language} onChange={onLanguageChange} />
              <Flex direction="column" align="end" gap="1">
                <Flex align="center" gap="2">
                  <Text size="1" align="right" color="gray">{localize('Auto listen')}</Text>
                  <Switch
                    size="2"
                    checked={recordMode === 'button'}
                    onCheckedChange={(checked) => onRecordModeChange(checked ? 'button' : 'auto')}
                    color="red"
                    className="record-mode-switch"
                  />
                  <Text size="1" color="gray">{localize('Record button')}</Text>
                </Flex>
              </Flex>
            </Flex>
          </Card>
          <Card>
            <Flex direction="column" gap="2">
              {isIos && !isStandalone ? (
                <>
                  <Text size="4">
                    {localize('To enable reminders, click the share button')}
                    {' '}
                    <Share2Icon width="20" height="20" style={{ verticalAlign: 'text-bottom' }} />
                    {', '}
                    {localize('then add to Home Screen.')}
                  </Text>
                  <Text size="1" color="gray">
                    {localize('NOTE: Home screen apps and Safari do not share data, log in to sync across devices.')}
                  </Text>
                </>
              ) : (
                <Button onClick={onTogglePush} variant={pushEnabled ? 'soft' : 'solid'}>
                  {pushEnabled ? localize('Disable reminders') : localize('Enable reminders')}
                </Button>
              )}
            </Flex>
          </Card>
          <Card>
            <Flex direction="column" gap="2">
              {!showLogin && !isSynced && <Button
                variant={'solid'}
                onClick={() => setShowLogin(true)}
              >
                {localize('Sync across devices')}
              </Button>}
              {(isSynced ? (
                <>
                  <Text size="1" color="gray">
                    {localize('Last synced')}: {formatSyncTime(lastSyncAt, showSyncingLabel || syncingNow)}
                  </Text>
                  {lastSyncError && (
                    <Text size="1" color="red">
                      {localize('Sync error')}: {lastSyncError}
                    </Text>
                  )}
                  <Flex gap="2">
                    {onSyncNow && (
                      <Button variant="soft" onClick={onSyncNowClick} disabled={syncingNow}>
                        {syncingNow ? localize('Syncing...') : localize('Sync now')}
                      </Button>
                    )}
                    <Button color="red" variant="soft" onClick={onSyncDisconnect}>
                      {localize('Disconnect sync')}
                    </Button>
                  </Flex>
                </>
              ) : (
                <>
                  {showLogin && !loginRequested && (
                    <>
                      <TextField.Root size="2" variant="surface">
                        <TextField.Input
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          placeholder="+1 555 555 5555"
                          value={syncPhone}
                          onChange={(event) => {
                            setSyncPhone(event.target.value);
                            if (syncError) {
                              setSyncError(null);
                            }
                          }}
                          onBlur={(event) => setSyncPhone(normalizePhoneInput(event.target.value))}
                        />
                      </TextField.Root>
                      {syncError && <Text size="1" color="red">{syncError}</Text>}
                      <Button
                        variant="solid"
                        onClick={onSyncStart}
                        disabled={!/^\+\d{10,15}$/.test(syncPhone.trim().replace(/[^\d+]/g, ''))}
                      >
                        {localize('Log in with phone')}
                      </Button>
                      <Text size="1" color="gray">
                        {localize('By clicking "Log in with phone", you agree to receive a one-time SMS verification code from DirtyLingo.')}
                      </Text>
                    </>
                  )}
                  {loginRequested && (
                    <>
                      <TextField.Root size="2" variant="surface">
                        <TextField.Input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder={localize('Verification code')}
                          value={smsCode}
                          onChange={(event) => {
                            setSmsCode(event.target.value);
                            if (syncError) {
                              setSyncError(null);
                            }
                          }}
                        />
                      </TextField.Root>
                      {syncError && <Text size="1" color="red">{syncError}</Text>}
                      <Flex gap="2">
                        <Button
                          variant="soft"
                          onClick={onSyncStart}
                          disabled={smsCodeCountdown > 0 || !/^\+\d{10,15}$/.test(syncPhone.trim().replace(/[^\d+]/g, ''))}
                        >
                          {smsCodeCountdown > 0
                            ? localize('Resend code in {time}')
                                .replace('{time}', `${Math.floor(smsCodeCountdown / 60)}:${String(smsCodeCountdown % 60).padStart(2, '0')}`)
                            : localize('Resend code')}
                        </Button>
                        <Button onClick={onSmsCodeVerify} disabled={smsCode.trim().length === 0}>
                          {localize('Verify')}
                        </Button>
                      </Flex>
                    </>
                  )}
                </>
              ))}
            </Flex>
          </Card>
          <Card>
            <Flex direction="column" gap="2">
              {!showDataDetails && <Button variant="soft" onClick={() => setShowDataDetails(true)}>
                {localize('Manage data')}
              </Button>}
              {showDataDetails && (
                <>
                  {stateMessage && <Text size="1" color="gray">{stateMessage}</Text>}
                  <Flex gap="2" wrap="wrap">
                    <Button variant="soft" onClick={onCopyStateClick}>
                      {localize('Copy conversation')}
                    </Button>
                    <Button variant="soft" onClick={onPasteStateClick}>
                      {localize('Paste conversation')}
                    </Button>
                  </Flex>
                  <Button color="red" variant="soft" onClick={onClearClick}>
                    {localize('Clear history')}
                  </Button>
                </>
              )}
            </Flex>
          </Card>
        </Flex>
        <Dialog.Description></Dialog.Description>
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button onClick={onDoneClick}>{doneText}</Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
